import { BookingLocationCodes } from '@fcom/common/interfaces/booking';
import { TripType } from '@fcom/core/interfaces';
import { TzDate } from '@fcom/core/utils';
import { GlobalBookingTripDates } from '@fcom/common';
import { getTripTypeFromBookingLocationCodes } from '@fcom/common-booking/utils/common-booking.utils';

import { ExtendedFlightBasket } from '../interfaces/upgrades.interface';
import { FlightsV2 } from '../api/models/flights-v-2';
import { FlightV2 } from '../api/models/flight-v-2';

export const flightsMaxPassengers = (flights: FlightsV2): number => {
  let hasWaitlistOption = false;

  const maxPax = flights.reduce((maxPax, flight) => {
    return flight.upgrades?.reduce((maxPax2, upgrade) => {
      if (upgrade.isWaitlist && upgrade.paymentOptions) {
        hasWaitlistOption = true;
      }

      return Math.max(maxPax2, upgrade.seatsAvailable);
    }, maxPax);
  }, 0);

  return hasWaitlistOption ? 99999 : maxPax;
};

export const flightsToJourneys = (flights: FlightsV2): FlightsV2[] => {
  const grouped = flights.reduce(
    (accumulator, flight): { [key: number]: FlightsV2[] } => ({
      ...accumulator,
      [flight.segmentInfo.group]: [...(accumulator[flight.segmentInfo.group] || []), { ...flight }],
    }),
    {}
  );

  return Object.keys(grouped).map((key) =>
    grouped[key].sort((a, b) => {
      if (a.segmentInfo.ordinal && b.segmentInfo.ordinal) {
        return a.segmentInfo.ordinal > b.segmentInfo.ordinal ? 1 : -1;
      }
    })
  );
};

export const getNumberOfFlightsInBound = (singleFlight: FlightV2, flights: FlightsV2): number => {
  let numberOfFlights = 0;

  flights.forEach((flight) => {
    if (singleFlight.segmentInfo.group === flight.segmentInfo.group) {
      numberOfFlights++;
    }
  });

  return numberOfFlights;
};

export const filterSelectedJourneys = (journeys: FlightsV2[], flightBasket: ExtendedFlightBasket[]): FlightsV2[] => {
  const selectedIds = flightBasket.map((flight) => flight.flightId);

  return journeys
    .map((flights) => {
      return flights.filter((flight) => {
        return selectedIds.includes(flight.segmentInfo.flightId);
      });
    })
    .filter((journey) => journey.length);
};

export const getFlightsAsBookingLocationCodes = (flights: FlightsV2): BookingLocationCodes[] => {
  return flightsToJourneys(flights).map((segments: FlightsV2) => ({
    originLocationCode: segments[0].segmentInfo.boardPoint,
    destinationLocationCode: segments[segments.length - 1].segmentInfo.offPoint,
  }));
};

export const getDatesForJourney = (flights: FlightsV2, tripType: TripType): GlobalBookingTripDates => {
  const groupedFlights = flightsToJourneys(flights);
  const departureFlight = groupedFlights[0][0];

  const returningFlight = groupedFlights[groupedFlights.length - 1][0];
  const dates: GlobalBookingTripDates = {
    departureDate: new TzDate(departureFlight.segmentInfo.departureDateTime).toLocalDate(),
  };

  if (tripType !== TripType.ONEWAY) {
    dates.returnDate = new TzDate(returningFlight.segmentInfo.departureDateTime).toLocalDate();
  }

  return dates;
};

export const getLocationsForGAString = (flights: FlightsV2): string => {
  const locations = getFlightsAsBookingLocationCodes(flights);
  const tripType = getTripTypeFromBookingLocationCodes(locations);

  if (tripType !== TripType.MULTICITY) {
    return `${locations[0].originLocationCode}-${locations[0].destinationLocationCode}`;
  }

  const multiCityOrigins = locations.map((location) => location.originLocationCode).join('-');

  return `${multiCityOrigins}-${locations[locations.length - 1].destinationLocationCode}`;
};
